import type { MeasurementReviewField } from "./measurement-review";

/** Editing a generated scope is not a request to run description AI again. */
export function descriptionRemainsReady(ready: boolean, description: string) {
  return ready && description.trim().length > 0;
}

/** An old minimum checklist must not re-add work the builder has removed. */
export function descriptionPricingInputs<T>(
  description: string, generatedDescription: string | null, workItems: T[], preparedQuote: unknown,
) {
  const unchanged = generatedDescription !== null && description === generatedDescription;
  return {
    workItems: unchanged && workItems.length ? workItems : undefined,
    preparedQuote: unchanged ? preparedQuote ?? undefined : undefined,
  };
}

// Be conservative about changed scope/numbers, but do not repeat review just
// because of whitespace, case or punctuation changes.
export function measurementConfirmationKey(
  description: string, projectTypes: string[], imageIds: string[], fields: MeasurementReviewField[],
) {
  const words = description.toLowerCase().match(/[\p{L}\p{N}]+(?:[.,]\d+)?/gu) ?? [];
  return JSON.stringify([words, projectTypes, imageIds, fields]);
}

export interface DescriptionRequestIdentity {
  id: string;
  ownerId: string | undefined;
  description: string;
  imageIds: string[];
  editRevision: number;
}

export function acceptsDescriptionResult(
  request: DescriptionRequestIdentity, activeId: string | undefined,
  current: Omit<DescriptionRequestIdentity, "id">,
) {
  return request.id === activeId && request.ownerId === current.ownerId
    && request.description === current.description && request.editRevision === current.editRevision
    && JSON.stringify(request.imageIds) === JSON.stringify(current.imageIds);
}

export function readDescriptionResult(text: string) {
  let result: any;
  try {
    result = JSON.parse(text.trim());
  } catch {
    throw new Error("The description response could not be read. Your project details are still here; please retry.");
  }
  if (!result || typeof result !== "object" || result.error) {
    throw Object.assign(new Error(result?.message || "The description could not be completed. Your project details are still here; please retry."),
      { code: result?.code });
  }
  return result;
}
